import React, {useState} from "react";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../../../../redux/store";

// Import types e actions
import {Assegnazione, createAssegnazioneVuota, OrdineLavoro} from "../../ordiniLavoroTypes";
import {deleteAssegnazione} from "../../ordiniLavoroSlice";
import {Macchina} from "components/anagrafiche/anagraficheSlice";
import {ErrorStatusTypes} from "components/common/errorsDeclarations";

// Material UI
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import {useTheme} from "@mui/material/styles";

// Icons
import AddIcon from "@mui/icons-material/Add";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";

// Components
import AssegnazioneInlineForm from "./AssegnazioneInlineForm";
import {Chip} from "@mui/material";

// ============================================
// INTERFACES
// ============================================

interface AssegnazioniCardProps {
    ordineLavoro: OrdineLavoro;
}

// ============================================
// COMPONENT
// ============================================

const AssegnazioniCard = ({ordineLavoro}: AssegnazioniCardProps) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();

    // Stato locale per nuova assegnazione in creazione
    const [nuovaAssegnazione, setNuovaAssegnazione] = useState<Assegnazione | null>(null);

    // ============================================
    // SELECTORS
    // ============================================

    const macchine = useSelector<RootState, Macchina[]>(
        (state) => state.anagraficheState?.macchine?.results || []
    );

    const ordineLavoroStatus = useSelector<RootState, ErrorStatusTypes>(
        (state) => state.ordiniLavoroState.ordiniLavoroList.errorsStack.status
    );

    const isLoading = ordineLavoroStatus === ErrorStatusTypes.PENDING;

    // ============================================
    // HANDLERS
    // ============================================

    const handleAggiungiAssegnazione = () => {
        setNuovaAssegnazione(createAssegnazioneVuota(ordineLavoro.id));
    };

    const handleAnnullaNuova = () => {
        setNuovaAssegnazione(null);
    };

    const handleSaveSuccess = () => {
        // Reset nuova assegnazione dopo salvataggio
        setNuovaAssegnazione(null);
    };

    const handleDeleteAssegnazione = async (assegnazioneId: number) => {
        if (window.confirm("Sei sicuro di voler eliminare questa assegnazione?")) {
            ordineLavoro.id && await dispatch(deleteAssegnazione({
                ordineLavoroId: ordineLavoro.id,
                assegnazioneId,
            }));
        }
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <Card sx={{mt: 5, backgroundColor: theme.palette.background.default}}>
            <CardContent>
                {/* Header */}
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography variant="subtitle1" fontWeight="bold">
                        Assegnazioni Macchine
                    </Typography>
                    <Chip label={
                        <>
                            <Typography variant="caption" display="inline">
                                confermato:
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="bold" display="inline">
                                {ordineLavoro.valore_confermato && Intl.NumberFormat("it-IT", {
                                    style: "decimal",
                                    useGrouping: true,
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                }).format(ordineLavoro.valore_confermato)} €
                            </Typography>
                        </>}
                          color="info"
                    />
                    <Chip label={
                        <>
                            <Typography variant="caption" display="inline">
                                schedulato:
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="bold" display="inline">
                                {ordineLavoro.valore_schedulato && Intl.NumberFormat("it-IT", {
                                    style: "decimal",
                                    useGrouping: true,
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                }).format(ordineLavoro.valore_schedulato)} €
                            </Typography>
                        </>}
                          color="primary"
                    />
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon/>}
                        onClick={handleAggiungiAssegnazione}
                        disabled={isLoading || nuovaAssegnazione !== null}
                    >
                        Aggiungi
                    </Button>
                </Stack>

                <Divider sx={{mb: 2}}/>

                {/* Loading */}
                {isLoading && (
                    <Box sx={{display: "flex", justifyContent: "center", py: 2}}>
                        <CircularProgress size={24}/>
                    </Box>
                )}

                {/* Lista Assegnazioni */}
                {ordineLavoro.assegnazioni && ordineLavoro.assegnazioni.length === 0 && !nuovaAssegnazione && (
                    <Box sx={{textAlign: "center", py: 4}}>
                        <PrecisionManufacturingIcon
                            sx={{fontSize: 48, color: theme.palette.grey[400], mb: 1}}
                        />
                        <Typography variant="body2" color="text.secondary">
                            Nessuna assegnazione presente
                        </Typography>
                    </Box>
                )}

                {/* Form per nuova assegnazione */}
                {nuovaAssegnazione && ordineLavoro.id && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 2,
                            backgroundColor: theme.palette.action.hover,
                            borderRadius: 1,
                        }}
                    >
                        <AssegnazioneInlineForm
                            assegnazione={nuovaAssegnazione}
                            ordineLavoro={ordineLavoro}
                            macchine={macchine}
                            isNew={true}
                            onCancel={handleAnnullaNuova}
                            onSaveSuccess={handleSaveSuccess}
                        />
                    </Box>
                )}

                {/* Assegnazioni esistenti */}
                {ordineLavoro.assegnazioni?.map((assegnazione, index) => (
                    <Box
                        key={assegnazione.id}
                        sx={{
                            mb: index < (ordineLavoro.assegnazioni?.length || 0) - 1 ? 1 : 0,
                            p: 1.5,
                            backgroundColor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 1,
                        }}
                    >{ordineLavoro.id &&
                        <AssegnazioneInlineForm
                            assegnazione={assegnazione}
                            ordineLavoro={ordineLavoro}
                            macchine={macchine}
                            isNew={false}
                            onDelete={() => handleDeleteAssegnazione(assegnazione.id!)}
                        />
                    }
                    </Box>
                ))}
            </CardContent>
        </Card>
    )
        ;
};

export default AssegnazioniCard;